import { ReactComponent as InProgressIcon } from 'assets/icons/in_progress.svg';
import { ReactComponent as ItemDoneIcon } from 'assets/icons/itemDone.svg';
import clsx from 'clsx';
import Button from 'components/Button/Button';
import { INITIATE } from 'constants/general';
import { BUTTON_VARIANT } from 'constants/interfaces';
import { INSIGHT_ITEMS, INSIGHT_ITEMS_TYPES, STATUS_SECTIONS } from 'constants/materialAssessment';

import type { IStepsListItem } from './interfaces/IStepsList';
import type IStepsList from './interfaces/IStepsList';

import styles from './sass/StepsList.module.scss';

function StepsList({
  step, handleStepChange, stepsList, disableList, optionList, optionListVM,
  sectionItems, activeClick, hideColor, recordId, statusActiveButton,
}: IStepsList) {
  const getAllSteps = (): string[] => {
    const allSteps: string[] = [];

    stepsList.forEach((stepItem) => {
      if (stepItem.items && stepItem.items.length > 0) {
        stepItem.items.forEach((item) => {
          allSteps.push(item.value);
        });
      } else {
        allSteps.push(stepItem.value);
      }
    });

    return allSteps;
  };

  const checkStepValid = (stepValue: string, value: string): boolean => {
    const allSteps = getAllSteps();
    const valueIndex = allSteps.indexOf(value);
    const stepIndex = allSteps.indexOf(stepValue);

    return valueIndex !== -1 && stepIndex !== -1 && valueIndex < stepIndex;
  };

  const isDisabled = (value: string): boolean => disableList?.includes(value) ?? false;

  const isStatusActive = (status: string): boolean => (
    status === STATUS_SECTIONS.COMPLETED || status === STATUS_SECTIONS.IN_PROGRESS
  );

  const getStatusFromSectionItems = (type?: string): string => {
    if (!type) return STATUS_SECTIONS.EMPTY;

    const foundItem = optionListVM?.find((listItem) => listItem.type === type);
    return foundItem?.status ?? STATUS_SECTIONS.EMPTY;
  };

  const getStatusFromInsightItems = (type: string): string => {
    const insightsItem = optionList?.find((listItem) => listItem.type === INSIGHT_ITEMS_TYPES.INSIGHTS);
    const subSection = insightsItem?.subSections?.find((subItem) => subItem.type === type);

    return subSection?.status ?? STATUS_SECTIONS.EMPTY;
  };

  const getStatusFromOptionList = (type?: string): string => {
    if (!type) return STATUS_SECTIONS.EMPTY;

    const foundItem = optionList?.find((listItem) => listItem.type === type);
    return foundItem?.status ?? STATUS_SECTIONS.EMPTY;
  };

  const getStatus = (type?: string, sectionType?: string): string => {
    if (sectionType) {
      return getStatusFromSectionItems(type);
    }

    if (!sectionType && recordId && type === INSIGHT_ITEMS_TYPES.INITIATE) {
      return STATUS_SECTIONS.COMPLETED;
    }

    if (type && INSIGHT_ITEMS.includes(type)) {
      return getStatusFromInsightItems(type);
    }

    return getStatusFromOptionList(type);
  };

  const getStatusIcon = (status: string): React.JSX.Element | null => {
    switch (status) {
      case STATUS_SECTIONS.IN_PROGRESS:
        return <InProgressIcon />;
      case STATUS_SECTIONS.COMPLETED:
        return <ItemDoneIcon />;
      default:
        return null;
    }
  };

  const onGetIconStatus = (type?: string, sectionType?: string): React.JSX.Element | null => {
    const status = getStatus(type, sectionType);
    return getStatusIcon(status);
  };

  const handleCompleteStatus = (type?: string): boolean => {
    if (type === INITIATE) {
      return true;
    }

    if (type && INSIGHT_ITEMS.includes(type)) {
      const status = getStatusFromInsightItems(type);
      return isStatusActive(status);
    }

    const status = getStatusFromOptionList(type);
    return isStatusActive(status);
  };

  const isButtonClickable = (itemType?: string): boolean => {
    if (activeClick) return true;
    if (statusActiveButton && handleCompleteStatus(itemType)) return true;

    return false;
  };

  const getButtonClickHandler = (value: string, itemType?: string) => {
    if (isButtonClickable(itemType)) {
      return () => handleStepChange(value);
    }

    return () => {};
  };

  const shouldShowFilledStyle = (itemValue: string, itemType?: string): boolean => {
    if (hideColor) return false;
    if (step === itemValue) return false;
    if (isDisabled(itemValue)) return false;
    if (!onGetIconStatus(itemType)) return false;
    return true;
  };

  const renderPhaseItems = (items: IStepsListItem[] | null): React.JSX.Element | null => {
    if (!items) return null;
    return (
      <div className={styles.phaseItems}>
        {items.map((item) => {
          const disabled = isDisabled(item.value);
          const icon = !disabled ? onGetIconStatus(item.type) : null;
          const isActive = step === item.value;
          const isFilled = shouldShowFilledStyle(item.value, item.type);
          return (
            <Button
              key={item.value}
              title={item.label}
              onClick={getButtonClickHandler(item.value, item.type)}
              variant={BUTTON_VARIANT.ghost}
              endIcon={icon}
              className={clsx(styles.stepButton, {
                [styles.active]: isActive,
                [styles.filledStep]: isFilled,
              })}
              disabled={disabled}
            />
          );
        })}
      </div>
    );
  };

  const renderPhaseWithItems = (stepItem: IStepsListItem): React.JSX.Element => (
    <>
      <div className={styles.phaseTitle}>{stepItem.label}</div>
      {renderPhaseItems(stepItem.items ?? null)}
    </>
  );

  const renderSingleStepButton = (stepItem: IStepsListItem): React.JSX.Element => {
    const disabled = isDisabled(stepItem.value);
    const itemType = stepItem.type || stepItem.value;
    const icon = !disabled ? onGetIconStatus(stepItem.type, sectionItems) : null;
    const isActive = step === stepItem.value;

    const isFilled = !hideColor
      && checkStepValid(step, stepItem.value)
      && step !== stepItem.value
      && !disabled;

    return (
      <Button
        title={stepItem.label}
        onClick={getButtonClickHandler(stepItem.value, itemType)}
        variant={BUTTON_VARIANT.ghost}
        endIcon={icon}
        className={clsx(styles.stepButton, {
          [styles.active]: isActive,
          [styles.filledStep]: isFilled,
        })}
        disabled={disabled}
      />
    );
  };

  return (
    <div className={styles.stepsList}>
      {stepsList.map((stepItem) => (
        <div key={stepItem.value}>
          {stepItem.items
            ? renderPhaseWithItems(stepItem)
            : renderSingleStepButton(stepItem)}
        </div>
      ))}
    </div>
  );
}

export default StepsList;
